import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { Loader2, RefreshCcw } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHead, Stat } from "../../components/Bits";

const PALETTE = ["#0D9488", "#0F172A", "#DC2626", "#D97706", "#2563EB", "#7C3AED", "#64748B"];
const Card = ({ title, children, className = "", testid }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`} data-testid={testid}><div className="overline mb-4">{title}</div>{children}</div>
);
const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/stats").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  const c = d.cards;
  const createRe = async (iid) => {
    try { const { data } = await api.post(`/inspections/${iid}/reinspection`, {}); toast.success(`Re-inspection ${data.inspection_id} scheduled`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead over="Bangalore operations" title="Dashboard">
        <Link to="/admin/inspections/new" className="tap flex h-11 items-center rounded-md bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-teal-700" data-testid="dashboard-new-inspection-button">New inspection</Link>
      </PageHead>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Total inspections" value={c.total} testid="kpi-total" />
        <Stat label="This month" value={c.this_month} testid="kpi-month" />
        <Stat label="Completed" value={c.completed} accent="text-emerald-600" testid="kpi-completed" />
        <Stat label="In progress" value={c.in_progress} sub={`${c.scheduled} scheduled`} accent="text-amber-600" testid="kpi-progress" />
        <Stat label="Reports generated" value={c.reports} testid="kpi-reports" />
        <Stat label="Major defects" value={c.major_defects} accent="text-rose-600" testid="kpi-major" />
        <Stat label="Re-inspections" value={c.reinspections} sub="incl. requests" testid="kpi-reinspections" />
        <Stat label="Revenue" value={inr(c.revenue)} sub={`Avg score ${c.avg_score}/100 · sample pricing`} accent="text-teal-700" testid="kpi-revenue" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card title="Inspections by month" className="lg:col-span-2" testid="chart-by-month">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.by_month}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#0D9488" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Defect distribution" testid="defect-distribution">
          <div className="space-y-3">
            {d.distribution.map((x, i) => (
              <div key={x.name}>
                <div className="flex justify-between text-sm"><span className="font-medium"><span className="mr-2 font-mono text-[10px] text-slate-400">#{i + 1}</span>{x.name}</span><span className="font-mono text-xs">{x.pct}%</span></div>
                <div className="mt-1 h-1.5 rounded bg-slate-100"><div className="h-full rounded" style={{ width: `${x.pct}%`, background: PALETTE[i % 7] }} /></div>
              </div>
            ))}
            <p className="pt-1 font-mono text-[10px] text-slate-500">{d.total_defects} observations · property quality dataset</p>
          </div>
        </Card>
        <Card title="Major defects by category" testid="chart-major-category">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.major_by_category} layout="vertical" margin={{ left: 20 }}><XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#DC2626" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Most common defects" testid="common-defects">
          <ol className="space-y-2">{d.common_defects.map((x, i) => <li key={x.name} className="flex items-center gap-3 text-sm"><span className="font-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span><span className="flex-1">{x.name}</span><span className="font-mono text-xs font-bold">{x.count}</span></li>)}</ol>
        </Card>
        <Card title="Inspections by location" testid="chart-location">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.by_location}><XAxis dataKey="location" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" radius={[4, 4, 0, 0]}>{d.by_location.map((x, i) => <Cell key={x.location} fill={PALETTE[i % 7]} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Inspector performance" className="lg:col-span-2" testid="inspector-performance">
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr className="border-b border-slate-200 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500"><th className="py-2">Inspector</th><th>Inspections</th><th>Completed</th><th>Defects logged</th><th>Avg score</th></tr></thead>
              <tbody>{d.inspector_performance.map((p) => <tr key={p.name} className="border-b border-slate-100"><td className="py-2.5 font-medium">{p.name}</td><td>{p.inspections}</td><td>{p.completed}</td><td>{p.defects}</td><td className="font-mono">{p.avg_score ?? "-"}</td></tr>)}</tbody></table>
          </div>
        </Card>
        <Card title="Open re-inspection requests" testid="reinspection-requests">
          <div className="space-y-2">
            {d.open_requests.map((r) => (
              <div key={r.inspection_id + r.at} className="rounded-md border border-slate-200 p-3 text-sm">
                <div className="font-mono text-xs font-bold">{r.inspection_id}</div><div>{r.customer} · {r.project}</div>
                <button onClick={() => createRe(r.inspection_id)} className="tap mt-2 flex h-9 items-center gap-1.5 rounded-md bg-slate-900 px-3 text-xs font-bold text-white" data-testid={`create-reinspection-${r.inspection_id}`}><RefreshCcw className="h-3.5 w-3.5" />Schedule re-inspection</button>
              </div>
            ))}
            {!d.open_requests.length && <p className="text-sm text-slate-500">No open requests.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
